import { useEffect } from 'react';
import { Box } from '@material-ui/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import am4lang_es_ES from '@amcharts/amcharts4/lang/es_ES';
import { getVariableUnit } from './helpers';

export default function ChartV2Advanced({
  chartData,
  chartOptions,
  height,
  ky,
  showLegend = false,
}) {
  const groupData = () => {
    const groupedData = {};

    let datasets = [];
    chartData.forEach(item => {
      let groupKey = '';
      groupKey = `${item.groupIndex}-${item.datasetIndex}`;
      // Si el grupo no existe en groupedData, lo inicializamos como un array vacío
      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      // Añadimos una propiedad groupArrayIndex para almacenar el índice dentro del grupo
      const groupArrayIndex = groupedData[groupKey].length;
      groupedData[groupKey].push({ ...item, groupArrayIndex });
    });

    datasets = Object.entries(groupedData).map(([key, value]) => ({
      groupKey: key,
      data: value,
    }));
    return datasets;
  };

  useEffect(() => {
    am4core.useTheme(am4themes_animated);
    let chart = am4core.create(`historicos${ky}`, am4charts.XYChart);
    let snapToSeries = [];

    if (chart.logo) {
      chart.logo.disabled = true;
    }
    chart.fontSize = 11;
    chart.language.locale = am4lang_es_ES;

    chart.exporting.menu = new am4core.ExportMenu();
    chart.exporting.adapter.add('data', function (data, target) {
      const myData = [];
      chart.series.each(function (series) {
        for (var i = 0; i < series.data.length; i++) {
          series.data[i].name = series.name;
          myData.push(series.data[i]);
        }
      });
      return { data: myData };
    });
    // Modifico el menu de exportación
    chart.exporting.menu.items = [
      {
        label: '...',
        menu: [
          { type: 'pdf', label: 'PDF' },
          { type: 'csv', label: 'CSV' },
          { type: 'xlsx', label: 'XLSX' },
          { type: 'print', label: 'Imprimir' },
        ],
      },
    ];

    if (showLegend) {
      chart.legend = new am4charts.Legend();
    }

    chart.hiddenState.properties.opacity = 0;

    // let dataseries = null;

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.dataFields.category = 'timestamp1';
    // Mostrar todas las etiquetas y rotarlas verticalmente
    categoryAxis.renderer.minGridDistance = 1;
    categoryAxis.renderer.labels.template.rotation = 90;
    categoryAxis.renderer.labels.template.verticalCenter = "middle";
    categoryAxis.renderer.labels.template.horizontalCenter = "left";


    const categoryAxis2 = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis2.renderer.grid.template.location = 0;
    categoryAxis2.dataFields.category = 'timestamp2';
    categoryAxis2.renderer.labels.template.disabled = true;


    const categoryAxis3 = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis3.renderer.grid.template.location = 0;
    categoryAxis3.dataFields.category = 'timestamp3';
    categoryAxis3.renderer.labels.template.disabled = true;


    let datasets = groupData();

    let dataseries1 = datasets.some(dataset =>
      dataset.data.some(item => item.hasOwnProperty('timestamp1'))
    );
    let dataseries2 = datasets.some(dataset =>
      dataset.data.some(item => item.hasOwnProperty('timestamp2'))
    );
    let dataseries3 = datasets.some(dataset =>
      dataset.data.some(item => item.hasOwnProperty('timestamp3'))
    );

    if (dataseries3) {
      resizeCells(0.07, 0.42, 0.32, 0.67, 0.57, 0.9);
    } else if (dataseries2) {
      resizeCells(0.06, 0.58, 0.42, 0.94);
    } else if (dataseries1) {
      resizeCells(0.05, 0.95);
    }

    const granularity = chartOptions.granularity;

    function createCustomAxis(titleText, format, index = 0) {
      const axis = chart.yAxes.push(new am4charts.ValueAxis());

      axis.title.text = titleText;

      axis.numberFormatter = new am4core.NumberFormatter();
      axis.numberFormatter.numberFormat = format;

      axis.renderer.minWidth = 40;
      axis.extraMax = 0.1;
      axis.calculateTotals = true;

      // MOSTRAR LA LÍNEA VERTICAL DEL EJE Y
      axis.renderer.line.strokeOpacity = 1;
      axis.renderer.line.strokeWidth = 1;

      // DESACTIVAR LÍNEAS HORIZONTALES DEL GRID
      // axis.renderer.grid.template.disabled = true;

      // Alternar izquierda/derecha para que no se monten
      axis.renderer.opposite = index % 2 === 1;

      return axis;
    }

    chart.data = chartData;

    // Mapa para guardar el eje Y por grupo
    const yAxesByGroup = {};
    // Mapa para llevar la cuenta de cuántas series van por grupo (para stacked)
    const groupSeriesCount = {};

    for (let index = 0; index < datasets.length; index++) {
      const groupIndex = datasets[index].data[0].groupIndex;
      const variable = datasets[index].data[0].variable;
      let currentValueAxis;

      // Si no existe un eje Y para este grupo, créalo
      if (!yAxesByGroup[groupIndex]) {
        let unit = getVariableUnit(variable);        
        yAxesByGroup[groupIndex] = createCustomAxis(transformText(variable), unit, groupIndex);
        groupSeriesCount[groupIndex] = 0;
      }
      currentValueAxis = yAxesByGroup[groupIndex];

      // Determina si es la primera serie del grupo
      const isFirstInGroup = groupSeriesCount[groupIndex] === 0;

      // Lógica de variable específica (como antes)
      switch (variable) {
        case 'POTENCIA_ADQUIRIDA_MAXIMA':
          addSeriesDefaultMethod(true, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'ENERGIA_REACTIVA_TOTAL':
          addSeriesDefaultMethod(true, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'COS_(FI)_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'TAN_(FI)_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'ENERGIA_ACTIVA_POR_FASE': {
          let kwh_r_data = datasets[index].data
            .filter(item => item.key === 'kwh_r')
            .map(item => ({ ...item, valor: item.valor * 1000 }));
          let kwh_s_data = datasets[index].data
            .filter(item => item.key === 'kwh_s')
            .map(item => ({ ...item, valor: item.valor * 1000 }));
          let kwh_t_data = datasets[index].data
            .filter(item => item.key === 'kwh_t')
            .map(item => ({ ...item, valor: item.valor * 1000 }));

          addSeries(
            kwh_r_data,
            `Fase R(${datasets[index].data[0].alias})`,
            adjustColorOpacity(datasets[index].data[0].color, 1),
            !isFirstInGroup,
            datasets[index].data[0].groupIndex,
            index,
            currentValueAxis
          );
          addSeries(
            kwh_s_data,
            `Fase S(${datasets[index].data[0].alias})`,
            adjustColorOpacity(datasets[index].data[0].color, 0.75),
            true,
            datasets[index].data[0].groupIndex,
            index,
            currentValueAxis
          );
          addSeries(
            kwh_t_data,
            `Fase T(${datasets[index].data[0].alias})`,
            adjustColorOpacity(datasets[index].data[0].color, 0.5),
            true,
            datasets[index].data[0].groupIndex,
            index,
            currentValueAxis
          );
          break;
        }
        case 'ENERGIA_REACTIVA_POR_FASE': {
          let kvarh_r_data = datasets[index].data
            .filter(item => item.key === 'kvarh_r')
            .map(item => ({ ...item, valor: item.valor * 1000 }));
          let kvarh_s_data = datasets[index].data
            .filter(item => item.key === 'kvarh_s')
            .map(item => ({ ...item, valor: item.valor * 1000 }));
          let kkvarh_t_data = datasets[index].data
            .filter(item => item.key === 'kvarh_t')
            .map(item => ({ ...item, valor: item.valor * 1000 }));

          addSeries(
            kvarh_r_data,
            `Fase R(${datasets[index].data[0].alias})`,
            adjustColorOpacity(datasets[index].data[0].color, 1),
            !isFirstInGroup,
            datasets[index].data[0].groupIndex,
            index,
            currentValueAxis
          );
          addSeries(
            kvarh_s_data,
            `Fase S(${datasets[index].data[0].alias})`,
            adjustColorOpacity(datasets[index].data[0].color, 0.75),
            true,
            datasets[index].data[0].groupIndex,
            index,
            currentValueAxis
          );
          addSeries(
            kkvarh_t_data,
            `Fase T(${datasets[index].data[0].alias})`,
            adjustColorOpacity(datasets[index].data[0].color, 0.5),
            true,
            datasets[index].data[0].groupIndex,
            index,
            currentValueAxis
          );
          break;
        }
        case 'ENERGIA_ACTIVA_IMPORTADA_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'ENERGIA_ACTIVA_EXPORTADA_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'ENERGIA_ACTIVA_GENERADA_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'ENERGIA_ACTIVA_CONSUMIDA_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        case 'ENERGIA_ACTIVA_AUTOCONSUMIDA_TOTAL':
          addSeriesDefaultMethod(false, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
        default:
          addSeriesDefaultMethod(true, datasets[index], index, currentValueAxis, !isFirstInGroup);
          break;
      }
      groupSeriesCount[groupIndex]++;
    }

    function transformText(text) {
      return text
        .toLowerCase()
        .split('_')
        .map(p => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    }

    function addSeries(
      serieData,
      serieName,
      serieColor,
      stacked = true,
      groupIndex,
      datasetIndex,
      customYAxis
    ) {
      const fmt = String(customYAxis?.numberFormatter?.numberFormat || '').trim();

      chart._yAxisByFormat = chart._yAxisByFormat || new Map();

      let yAxis = chart._yAxisByFormat.get(fmt);

      if (!yAxis) {
        yAxis = chart.yAxes?.values?.find(ax =>
          ax === customYAxis ||
          ax._formatKey === fmt ||
          (ax.numberFormatter && ax.numberFormatter.numberFormat === fmt)
        );
        if (yAxis) chart._yAxisByFormat.set(fmt, yAxis);
      }

      if (!yAxis && customYAxis) {
        customYAxis._formatKey = fmt;
        if (!customYAxis.numberFormatter) {
          const nf = new am4core.NumberFormatter();
          nf.numberFormat = fmt;
          customYAxis.numberFormatter = nf;
        }
        yAxis = customYAxis;
        chart._yAxisByFormat.set(fmt, yAxis);
      }

      if (!Array.isArray(serieData) || serieData.length === 0) return;

      const dataseries1 = serieData.filter(item => Object.prototype.hasOwnProperty.call(item, 'timestamp1'));
      const dataseries2 = serieData.filter(item => Object.prototype.hasOwnProperty.call(item, 'timestamp2'));
      const dataseries3 = serieData.filter(item => Object.prototype.hasOwnProperty.call(item, 'timestamp3'));

      const createColSeries = ({ data, categoryField, xAxis }) => {
        if (!data?.length) return;

        const exists = chart.series?.values?.some(s =>
          s.name === serieName && s.dataFields?.categoryX === categoryField
        );
        if (exists) return;

        const series = chart.series.push(new am4charts.ColumnSeries());
        series.columns.template.width = am4core.percent(60);
        series.dataFields.valueY = 'valor';
        series.dataFields.categoryX = categoryField;
        series.xAxis = xAxis;
        series.yAxis = yAxis;
        series.name = serieName;
        series.stacked = stacked;
        series.data = data;
        series.stroke = am4core.color(serieColor);
        series.columns.template.fill = am4core.color(serieColor);
        series.strokeWidth = 2;
        series.tooltip.pointerOrientation = 'vertical';
        series.tooltipText = `{valueY.formatNumber("${yAxis?.numberFormatter?.numberFormat || fmt}")} - ${serieName}`;
        series.tooltip.autoTextColor = false;

        series.legendSettings.labelText = serieName;
        series.legendSettings.valueText = `Grupo: ${groupIndex + 1} - Dataset: ${datasetIndex + 1}`;

        snapToSeries?.push(series);
      };

      createColSeries({ data: dataseries1, categoryField: 'timestamp1', xAxis: categoryAxis });
      createColSeries({ data: dataseries2, categoryField: 'timestamp2', xAxis: categoryAxis2 });
      createColSeries({ data: dataseries3, categoryField: 'timestamp3', xAxis: categoryAxis3 });
    }


    function resizeCells(start1, end1, start2, end2, start3, end3) {
      categoryAxis.renderer.cellStartLocation = start1;
      categoryAxis.renderer.cellEndLocation = end1;
      categoryAxis2.renderer.cellStartLocation = start2;
      categoryAxis2.renderer.cellEndLocation = end2;
      categoryAxis3.renderer.cellStartLocation = start3;
      categoryAxis3.renderer.cellEndLocation = end3;
    }

    function addSeriesDefaultMethod(multiplyValue, dataset, index, customYAxis, stacked) {
      let indexDataSet = 0;

      let actualIndexGroup = dataset.data[0].groupIndex;
      let actualDataGroup = dataset.data[0];
      addSeries(
        dataset.data.map(item => ({
          ...item,
          valor: multiplyValue ? item.valor * 1000 : item.valor,
        })),
        actualDataGroup.alias,
        actualDataGroup.color,
        stacked,
        actualIndexGroup,
        indexDataSet,
        customYAxis
      );
    }

    // Función para ajustar la opacidad de un color
    function adjustColorOpacity(color, opacity) {
      if (color.startsWith('#')) {
        // Convierte de HEX a RGBA
        const bigint = parseInt(color.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
      if (color.startsWith('rgb')) {
        // Convierte RGB a RGBA
        return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
      }
      // Devuelve el color original si no se puede procesar
      return color;
    }

    chart.strokeWidth = 1;
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.maxTooltipDistance = 20;
    return () => {
      chart.dispose();
    };
    // eslint-disable-next-line
  }, [chartData, chartOptions]);

  return (
    <Box style={{ height: height }}>
      <div
        id={`historicos${ky}`}
        style={{ height: height, width: '100%' }}
      />
    </Box>
  );
}
